import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getMyBlockingStatus } from '../api/Employee/onboardingProcesses';

// Routes that a blocked user can still access so they can resolve their blocking tasks.
const ALLOWED_PATHS = [
    '/onboarding/complete',
    '/terms-and-policies/accept',
    // The Pending Actions inbox lists the very items (terms, blocking onboarding
    // tasks) a blocked user must resolve — it must stay reachable while blocked.
    '/pending-actions',
    '/logout',
    '/callback',
    '/silent-renew',
];

// Module-level cache so acceptance flows (TermsAcceptanceView, MyOnboardingPage)
// can invalidate it after state-changing server calls. Without the invalidator
// there is a window (up to 30s) where the gate still thinks the user is
// blocked and bounces them back to /onboarding/complete, producing a
// root ↔ /onboarding/complete redirect loop right after accepting terms.
let blockingStatusCache: { ts: number; blocked: boolean } | null = null;

export function clearOnboardingGateCache() {
    blockingStatusCache = null;
}

/**
 * Redirects an authenticated user to /onboarding/complete whenever they have
 * unaccepted terms or an incomplete blocking onboarding task.
 */
const OnboardingGate: React.FC = () => {
    const location = useLocation();
    const [isBlocked, setIsBlocked] = useState<boolean | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function check() {
            // Cache for 30 seconds to avoid a request on every route change.
            const now = Date.now();
            if (blockingStatusCache && now - blockingStatusCache.ts < 30_000) {
                if (!cancelled) setIsBlocked(blockingStatusCache.blocked);
                return;
            }
            // Reset to null while the fetch is in flight so we don't render
            // <Outlet /> with a stale value. If we kept the prior value, a
            // blocked user navigating to `/` would briefly render
            // CompanyDashboard, which fires ~8 protected queries in parallel.
            // Those all 403 and each one triggers window.location.assign in
            // the axios interceptor — producing the reload storm users see
            // as an "infinite loop" after accepting T&C.
            if (!cancelled) setIsBlocked(null);
            try {
                const res = await getMyBlockingStatus();
                const blocked = !!res.payload?.isBlocked;
                blockingStatusCache = { ts: now, blocked };
                if (!cancelled) setIsBlocked(blocked);
            } catch {
                // On error, don't block — assume not blocked so the rest of the app remains usable.
                if (!cancelled) setIsBlocked(false);
            }
        }
        check();
        return () => { cancelled = true; };
    }, [location.pathname]);

    if (isBlocked === null) {
        // Render nothing until the gate has resolved. Children (dashboard,
        // MasterLayout hooks) must not mount while blocking status is unknown
        // — see the comment in the effect above.
        return null;
    }

    const onAllowedPath = ALLOWED_PATHS.some(p => location.pathname.startsWith(p));
    if (isBlocked && !onAllowedPath) {
        return <Navigate to="/onboarding/complete" replace />;
    }
    return <Outlet />;
};

export default OnboardingGate;
